// A project's risk score from its whole register, not its single worst item.
// Every open risk and issue adds weight; the total is squashed onto 0-100 so a long
// list of serious items reads worse than one, without ever passing 100.

export interface RegisterEntry {
  entry_type: 'risk' | 'issue' | 'assumption';
  rating: string | null;
  severity: string | null;
  score: number | null;
  status: string | null;
  response?: string | null;
  review_on?: string | null;
}

const WEIGHT = { critical: 10, high: 6, medium: 3, low: 1 } as const;
/** Weighted total at which a register scores about 63. */
const SCALE = 60;

export const isOpenEntry = (status: string | null) => !status || !/^(closed|resolved|done|retired|rejected)/.test(status);

const severityWord = (s: string | null) => s?.match(/\b(critical|high|medium|low)\b/i)?.[1].toLowerCase() as keyof typeof WEIGHT | undefined;

/** Weight of one entry: its rating, raised when escalated or past its review date, lowered when the risk is accepted. */
export function entryWeight(e: RegisterEntry, today: string): number {
  if (e.entry_type === 'assumption' || !isOpenEntry(e.status)) return 0;
  let base = 0;
  if (e.entry_type === 'issue') {
    const w = severityWord(e.severity);
    base = w ? WEIGHT[w] : 0;
  } else if (e.rating === 'high') {
    base = (e.score ?? 0) >= 20 ? WEIGHT.critical : WEIGHT.high;
  } else if (e.rating === 'medium') {
    base = WEIGHT.medium;
  } else if (e.rating === 'low') {
    base = WEIGHT.low;
  }
  if (base === 0) return 0;
  if (e.status === 'escalated') base *= 1.5;
  if (e.review_on && e.review_on < today) base *= 1.25;
  if (e.entry_type === 'risk' && e.response?.toLowerCase() === 'accept') base *= 0.7;
  return base;
}

/** 0-100. 0 when nothing is open. */
export function registerRiskScore(entries: RegisterEntry[], today = new Date().toISOString().slice(0, 10)): number {
  const total = entries.reduce((sum, e) => sum + entryWeight(e, today), 0);
  return total === 0 ? 0 : Math.round(100 * (1 - Math.exp(-total / SCALE)));
}

export interface RegisterDrivers {
  score: number;
  /** Open risks and issues, not assumptions. */
  open: number;
  critical: number;
  high: number;
  escalated: number;
  overdueReview: number;
}

/** The score and what drives it, for signals and explanations. */
export function registerDrivers(entries: RegisterEntry[], today = new Date().toISOString().slice(0, 10)): RegisterDrivers {
  const live = entries.filter((e) => e.entry_type !== "assumption" && isOpenEntry(e.status) && entryWeight(e, today) > 0);
  let critical = 0;
  let high = 0;
  for (const e of live) {
    const w = e.entry_type === "issue" ? severityWord(e.severity) : e.rating === "high" ? ((e.score ?? 0) >= 20 ? "critical" : "high") : null;
    if (w === "critical") critical++;
    else if (w === "high") high++;
  }
  return {
    score: registerRiskScore(entries, today),
    open: live.length,
    critical,
    high,
    escalated: live.filter((e) => e.status === "escalated").length,
    overdueReview: live.filter((e) => e.review_on && e.review_on < today).length,
  };
}
